/**
 * What an employee's list of payslips adds up to.
 *
 * Every figure here is derived from the payslips themselves rather than read
 * from a summary field, so the counters and the rows underneath can only ever
 * agree — a total that disagrees with the column it sits above is the specific
 * failure this exists to prevent.
 *
 * One rule governs the lot: an unpublished payslip does not count. It exists
 * as soon as the run is calculated, but the figure can still move, and quoting
 * a number that is not yet somebody's is worse than quoting none.
 */

/** Only payslips the employee has actually been given. */
export const publishedOnly = (payslips = []) => (payslips || []).filter((p) => p?.published);

/**
 * Newest first, by period rather than by insertion order.
 *
 * An off-cycle run can be created after a later month's regular run, so the
 * order the API returns them in is not the order they happened in.
 */
export const byPeriodDesc = (payslips = []) =>
  [...(payslips || [])].sort(
    (a, b) => (b.year - a.year) || (b.month - a.month)
  );

/** The most recent payslip the employee actually has. */
export const latestPayslip = (payslips = []) => byPeriodDesc(publishedOnly(payslips))[0] || null;

const sum = (rows, key) => rows.reduce((total, p) => total + (Number(p?.[key]) || 0), 0);

/**
 * The year to date, taken across the calendar year of the latest payslip.
 *
 * Calendar year, not the Indian financial year, and that is a deliberate limit
 * rather than an oversight: the financial year an employee cares about is the
 * one their tax is assessed on, and which one that is depends on the legal
 * entity's own settings. Labelling a calendar-year total as "this financial
 * year" would be wrong for every entity that does not start in January, so it
 * is labelled by the year it actually covers and the caller says so.
 */
export const yearToDate = (payslips = []) => {
  const published = publishedOnly(payslips);
  const latest = byPeriodDesc(published)[0];
  if (!latest) return null;
  const inYear = published.filter((p) => p.year === latest.year);
  return {
    year: latest.year,
    count: inYear.length,
    gross: sum(inYear, "grossPay"),
    deductions: sum(inYear, "totalDeductions"),
    net: sum(inYear, "netPay"),
  };
};

/**
 * When a payslip was paid, if it has been.
 *
 * The run's payment date is the fact; `publishedAt` is when it was shown to
 * the employee, which is usually the same day but is not the same thing and
 * must not be presented as one.
 */
export const paidOn = (payslip) => payslip?.payrollRun?.paymentDate || null;

/**
 * How many payslips are on file, as the header states it.
 *
 * Counts published ones only, so the number matches the rows the reader can
 * see. A count that includes a payslip they cannot open invites them to go
 * looking for it.
 */
export const availableLine = (payslips = []) => {
  const n = publishedOnly(payslips).length;
  if (!n) return "No payslips yet";
  return `${n} payslip${n === 1 ? "" : "s"} available`;
};
